import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { retentionFor } from '@content-engine/shared';
import { archiveEntity, fetchFilm } from '../../lib/queries.ts';
import type { FileState } from '../../lib/types.ts';
import { ArchiveDialog } from '../../components/archive/index.ts';

/**
 * Сколько живых потомков у фильма — ровно те числа, которые диалог показывает
 * («его 3 клипа и 7 вариантов останутся активными») и которыми блок способностей
 * объясняет, что архив ничего не отнимает. Считает БЭКЕНД (GET /films/:id →
 * counts, только активные): суммировать варианты по первой странице клипов
 * значило врать у фильма со 100+ клипами.
 */
export function useFilmChildCounts(filmId: string): { clips: number; variants: number } {
  const q = useQuery({
    queryKey: ['films', filmId],
    queryFn: () => fetchFilm(filmId),
    enabled: filmId !== '',
    staleTime: 30_000,
  });
  return {
    clips: q.data?.counts.clips ?? 0,
    variants: q.data?.counts.variants ?? 0,
  };
}

interface Props {
  filmId: string;
  /** Исходник фильма (роль film_source): его объём и есть «Освободит …». */
  file: FileState;
  onClose: () => void;
  onArchived?: () => void;
}

/** «В архив» для фильма: одна модалка + вызов POST /archive/film/:id. */
export function FilmArchiveDialog({ filmId, file, onClose, onArchived }: Props): React.ReactElement {
  const queryClient = useQueryClient();
  const children = useFilmChildCounts(filmId);

  const mutation = useMutation({
    mutationFn: (opts: { freeFiles: boolean }) => archiveEntity('film', filmId, opts),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['films'] });
      onClose();
      onArchived?.();
    },
  });

  return (
    <ArchiveDialog
      kind="film"
      freeAfterDays={retentionFor('film_source')}
      sizeBytes={file.sizeBytes}
      clipsCount={children.clips}
      variantsCount={children.variants}
      busy={mutation.isPending}
      onCancel={onClose}
      onConfirm={(opts) => mutation.mutate(opts)}
    />
  );
}
